import {
    Edit,
    Launch,
    Save,
    SettingsSuggest,
    Verified,
} from '@mui/icons-material';
import { Button, IconButton } from '@mui/material';
import { getNewPortalUsers } from 'lib/firebase';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from 'store';
import { retrievedNewPortalUsers } from 'store/features/authentication';
import { Ticket } from 'types/User/firebaseTypes/ticket';
import styles from './DeliveryDetailsModal.module.scss';
import * as yup from 'yup';
import { DefaultOpenModalProps } from 'components/button/OpenModalButton/OpenModalButton';
import { FormModal } from 'components/form/FormModal/FormModal';
import {
    FormDatePicker,
    FormInputGroup,
    FormSubmitButton,
    FormText,
} from 'components/form';
import {
    convertDeliveryStatusToInternalName,
    deliveryStatusDisplayNames,
} from 'lib/constants';
import { FormSelect } from 'components/form/FormSelect/FormSelect';
import { FieldValues } from 'react-hook-form';
import { makeDateConsistent } from 'helpers/dates';
import { normalizeDeliveryTicket } from 'helpers/planning';
import { Device } from 'types';
import { useLazyGetDeviceByShortIdEsQuery } from 'store/services';
import { useNavigate } from 'react-router-dom';
import { FormAutocomplete } from 'components/form/FormAutocomplete/FormAutocomplete';
import { maxValidFill } from 'helpers/planning';
import { format } from 'date-fns';
import { activateSnackbar } from 'store/features/ui';
import {
    useLazyResolveDeliveryTicketQuery,
    useLazyUpdateDeliveryTicketQuery,
    useLazyUpdateResolvedDeliveryTicketQuery,
} from 'store/services/planning';
import { CreatedBy } from 'components/common/CreatedBy/CreatedBy';
import { isResolved } from 'helpers/tickets';

type DeliveryTicketInput = Omit<Partial<Ticket>, 'taggedUsers'> & {
    taggedUsers: string;
};

const deliverySchema = yup.object({
    taggedUsers: yup.string().nullable(),
    status: yup.string(),
    plannedDate: yup.date().nullable(),
    plannedGallons: yup.number().nullable(),
    dateDelivered: yup.date().nullable().when('status', {
        is: 'done',
        then: yup.date().required(),
    }),
    gallonsDelivered: yup
        .number()
        .nullable()
        .when('status', {
            is: 'done',
            then: yup
                .number()
                .moreThan(
                    0,
                    'Gallons delivered must be greater than 0 for tickets with status "Done." Consider changing status to "Archived" if gallons delivered is 0.'
                )
                .required(),
        }),
    delivery_instructions: yup.string().nullable(),
});

interface DeliveryDetailsModalProps extends DefaultOpenModalProps {
    ticket: Ticket;
    device: Device;
    setActiveTicket: (t: Ticket | null) => void;
    setDeliveries?: (
        d: Ticket[] | ((deliveries: Ticket[]) => Ticket[])
    ) => void;
}

export const DeliveryDetailsModal = ({
    modalOpen,
    setModalOpen,
    setActiveTicket,
    setDeliveries,
    ticket,
    device,
}: DeliveryDetailsModalProps) => {
    const [isEdit, setIsEdit] = useState<boolean>(false);

    const defaults: DeliveryTicketInput = {
        taggedUsers: ticket?.taggedUsers?.[0]?.uid || '',
        status: convertDeliveryStatusToInternalName[ticket?.status ?? ''],
        plannedDate: makeDateConsistent(ticket?.plannedDate) || new Date(),
        plannedGallons: +Number(
            ticket?.plannedGallons || maxValidFill(device) || 0
        ).toFixed(2),
        dateDelivered: makeDateConsistent(ticket?.dateDelivered) || new Date(),
        gallonsDelivered: !isNaN(Number(ticket?.gallonsDelivered))
            ? +Number(ticket?.gallonsDelivered).toFixed(2)
            : 0,
        delivery_instructions: ticket?.delivery_instructions || '',
    };

    const [defaultDeliveryValues, setDefaultDeliveryValues] =
        useState<DeliveryTicketInput>(defaults);

    const authentication = useSelector(
        (state: RootState) => state.authentication
    );

    const userOptions = authentication.newPortalUsers
        ? Object.keys(authentication.newPortalUsers).map((key: string) => {
              return {
                  name: authentication.newPortalUsers?.[key]?.email || '',
                  value: authentication.newPortalUsers?.[key]?.uid || '',
              };
          })
        : [];

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const onClose = () => {
        setIsEdit(false);
        setModalOpen(false);
    };

    const [getDeviceQuery] = useLazyGetDeviceByShortIdEsQuery();
    const [updateDeliveryQuery] = useLazyUpdateDeliveryTicketQuery();
    const [updateResolvedTicketQuery] =
        useLazyUpdateResolvedDeliveryTicketQuery();
    const [resolveDeliveryTicket] = useLazyResolveDeliveryTicketQuery();

    useEffect(() => {
        if (ticket && modalOpen) {
            const ticketFormValues = {
                taggedUsers: ticket?.taggedUsers?.[0]?.uid || '',
                status: convertDeliveryStatusToInternalName[ticket?.status],
                plannedDate:
                    makeDateConsistent(ticket?.plannedDate ?? device?.duf_7) ||
                    new Date(),
                plannedGallons: +Number(
                    ticket?.plannedGallons || maxValidFill(device) || 0
                ).toFixed(2),
                dateDelivered:
                    makeDateConsistent(ticket?.dateDelivered) || new Date(),
                gallonsDelivered: !isNaN(Number(ticket?.gallonsDelivered))
                    ? +Number(ticket?.gallonsDelivered).toFixed(2)
                    : 0,
                delivery_instructions:
                    ticket?.delivery_instructions ||
                    device?.delivery_instructions,
            };
            setDefaultDeliveryValues(ticketFormValues);
        }
    }, [ticket, modalOpen, authentication.user, getDeviceQuery, device]);

    useEffect(() => {
        if (!!authentication.supplierId && !authentication.newPortalUsers) {
            getNewPortalUsers(authentication.supplierId).then(
                (newPortalUsers) => {
                    dispatch(retrievedNewPortalUsers(newPortalUsers));
                }
            );
        }
    }, [authentication, dispatch]);

    useEffect(() => {
        setIsEdit(false);
        if (!modalOpen) {
            setDefaultDeliveryValues(defaults);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalOpen]);

    const onConfirmPress = () => {
        const currentCreatedBy = ticket?.createdBy;

        if (!ticket) {
            return;
        }

        if (
            currentCreatedBy !== 'Auto' &&
            currentCreatedBy !== 'Confirmed Auto'
        ) {
            return dispatch(
                activateSnackbar({
                    message: 'Ticket not automatically generated.',
                    severity: 'error',
                })
            );
        }

        const newTicket: Ticket = {
            ...ticket,
            createdBy: currentCreatedBy === 'Auto' ? 'Confirmed Auto' : 'Auto',
        };

        if (
            authentication?.supplierId ||
            authentication?.supplierId?.toString() === '0'
        ) {
            const updateRequest =
                newTicket.status === 'done'
                    ? updateResolvedTicketQuery
                    : updateDeliveryQuery;

            Promise.resolve(
                updateRequest({
                    supplierId: authentication.supplierId,
                    deviceId: ticket.deviceId,
                    newTicket: normalizeDeliveryTicket(newTicket),
                })
            ).then((result) => {
                if (result && result.data) {
                    setActiveTicket(result.data);
                    if (setDeliveries) {
                        setDeliveries((deliveries: Ticket[]) => {
                            const newDeliveries = [...deliveries];

                            const index = newDeliveries.findIndex(
                                (delivery) => delivery.id === result.data?.id
                            );

                            if (index > -1 && result.data) {
                                newDeliveries.splice(index, 1, result.data);
                            }

                            return newDeliveries;
                        });
                    }
                    dispatch(
                        activateSnackbar({
                            message: 'Ticket confirmed',
                            severity: 'success',
                        })
                    );
                }
            });
        }
    };

    const onSubmit = (data: FieldValues) => {
        if (
            authentication?.supplierId &&
            authentication.newPortalUsers &&
            data &&
            ticket
        ) {
            const selectedUser = data.taggedUsers
                ? authentication.newPortalUsers[data.taggedUsers]
                : null;
            const ticketReq: Ticket = {
                ...ticket,
                ...{
                    ...data,
                    plannedDate: new Date(data.plannedDate),
                    dateDelivered: new Date(data.dateDelivered),
                    ticketType: 'fill',
                },
            };

            ticketReq.taggedUsers = selectedUser ? [selectedUser] : [];

            ticketReq.delivery_instructions = data.delivery_instructions ?? '';

            // Just a note that this does remove dateDelivered and gallonsDelivered for tickets not marked as ‘done'
            const normalizedTicket = normalizeDeliveryTicket(ticketReq);

            const needsResolving =
                ticket.ticketDirectory === 'active' &&
                isResolved(normalizedTicket); // does the new ticket have either 'done' or 'archived'

            if (needsResolving) {
                normalizedTicket.tankCapacity = device.capacity;
            }

            const updateRequest =
                normalizedTicket.status === 'done'
                    ? needsResolving
                        ? resolveDeliveryTicket
                        : updateResolvedTicketQuery
                    : updateDeliveryQuery;

            Promise.resolve(
                updateRequest({
                    supplierId: authentication.supplierId,
                    deviceId: ticket.deviceId,
                    newTicket: normalizedTicket,
                })
            ).then((result) => {
                if (result && result.data) {
                    setActiveTicket(result.data);
                }
                if (result && result.data && setDeliveries) {
                    setDeliveries((deliveries: Ticket[]) => {
                        const newDeliveries = [...deliveries];

                        const index = newDeliveries.findIndex(
                            (delivery) => delivery.id === result.data?.id
                        );

                        if (index > -1 && result.data) {
                            newDeliveries.splice(index, 1, result.data);
                        } else if (result.data) {
                            newDeliveries.splice(0, 1, result.data);
                        }

                        return newDeliveries;
                    });
                }
                dispatch(
                    activateSnackbar({
                        message: 'Ticket successfully updated',
                        severity: 'success',
                    })
                );
                onClose();
            });
        } else {
            dispatch(
                activateSnackbar({
                    message: 'Something went wrong, please try again.',
                    severity: 'error',
                })
            );
        }
    };

    const onError = (error: FieldValues) => {
        const messages = Object.keys(error).map((key) => error[key]?.message);
        dispatch(
            activateSnackbar({
                message: JSON.stringify(messages),
                severity: 'error',
            })
        );
    };

    const onFormModalClose = () => {
        setActiveTicket(null);
    };

    const isAuto = ticket?.createdBy === 'Auto';
    const isConfirmed = ticket?.createdBy === 'Confirmed Auto';

    return (
        <div data-testid="DeliveryDetailsModal">
            <FormModal
                defaultValues={defaultDeliveryValues}
                validationSchema={deliverySchema}
                onErrorHandler={onError}
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
                onClose={onFormModalClose}
                title={
                    <div className={styles.titleContainer}>
                        <div className={styles.title}>
                            Delivery Ticket Details{' '}
                            <CreatedBy ticket={ticket} showIcon />{' '}
                        </div>
                        <div>
                            Monitor: {device?.name ?? device?.shortDeviceId}{' '}
                            <IconButton
                                onClick={() => {
                                    onClose();
                                    navigate(
                                        `/device-details/${device?.deviceId}`,
                                        {
                                            state: device,
                                        }
                                    );
                                }}
                                className={styles.linkButton}
                            >
                                <Launch />
                            </IconButton>{' '}
                            | Customer:{' '}
                            {device?.customer_name ||
                                device?.protected?.supplierId}
                        </div>
                    </div>
                }
                hideDefaultSubmit
                actionChildren={
                    <div className={styles.modalActions}>
                        <div className={styles.estimations}>
                            <strong>Initial Estimates</strong>
                            <div>
                                <>
                                    Estimated Delivery Date:{' '}
                                    {device?.duf_7
                                        ? format(device?.duf_7, 'MM/dd/yyyy')
                                        : 'N/A'}
                                </>
                            </div>
                            <div>
                                <>
                                    Estimated Delivery Size:{' '}
                                    {!!device && !isNaN(maxValidFill(device))
                                        ? `${maxValidFill(device).toFixed(
                                              2
                                          )} gal.`
                                        : 'N/A'}
                                </>
                            </div>
                        </div>
                        <div>
                            {(isAuto || isConfirmed) && (
                                <Button
                                    variant="contained"
                                    color={isAuto ? 'success' : 'warning'}
                                    onClick={onConfirmPress}
                                    title={
                                        isAuto
                                            ? 'This ticket was automatically generated. Click here to mark it as confirmed.'
                                            : 'This automatically-generated ticket was previously marked confirmed. Click here to undo this action.'
                                    }
                                    disabled={isEdit} // Because updating the ticket changes the default values. Figure it's better to avoid confusion and just disable it if the user is changing other values
                                    startIcon={
                                        isAuto ? (
                                            <Verified />
                                        ) : (
                                            <SettingsSuggest />
                                        )
                                    }
                                >
                                    {isAuto ? 'Confirm' : 'Deconfirm'}
                                </Button>
                            )}
                            {!isEdit ? (
                                <Button
                                    variant="contained"
                                    onClick={() => setIsEdit(true)}
                                    startIcon={<Edit />}
                                >
                                    Edit
                                </Button>
                            ) : (
                                <FormSubmitButton
                                    onErrorHandler={onError}
                                    onValidHandler={onSubmit}
                                    fullWidth={false}
                                    buttonLabel="Save"
                                    startIcon={<Save />}
                                />
                            )}
                        </div>
                    </div>
                }
            >
                <FormInputGroup>
                    <FormSelect
                        field="status"
                        options={deliveryStatusDisplayNames.map((status) => ({
                            name: status,
                            value: convertDeliveryStatusToInternalName[status],
                        }))}
                        disabled={!isEdit}
                    />
                </FormInputGroup>
                <FormInputGroup>
                    <FormDatePicker field="plannedDate" disabled={!isEdit} />
                    <FormText
                        field="plannedGallons"
                        isNumber
                        disabled={!isEdit}
                        label="Gallons Planned"
                    />
                </FormInputGroup>
                <FormInputGroup>
                    <FormDatePicker field="dateDelivered" disabled={!isEdit} />
                    <FormText
                        field="gallonsDelivered"
                        isNumber
                        disabled={!isEdit}
                    />
                </FormInputGroup>
                <FormInputGroup>
                    <FormAutocomplete
                        label="Driver"
                        field="taggedUsers"
                        options={[{ name: '', value: '' }, ...userOptions]}
                        disabled={!isEdit}
                    />
                    <FormText
                        isTextArea
                        field="delivery_instructions"
                        disabled={!isEdit}
                    />
                </FormInputGroup>
            </FormModal>
        </div>
    );
};
